import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { INDUSTRIES, JOB_TYPES, SELECT_CONTENT_LIGHT } from "@/lib/constants";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { JobCard } from "@/components/site/JobCard";
import { Seo } from "@/components/site/Seo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Careers() {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const [type, setType] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (industry !== "all") params.industry = industry;
    if (type !== "all") params.type = type;
    try {
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } catch {
      setJobs([]);
    }
  }, [q, industry, type]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <main data-testid="careers-page" className="pt-40 pb-32">
      <Seo
        title="Open Roles — Careers via Samanvay HR"
        description="Browse live job openings across IT, BFSI, healthcare, retail and more. Apply in two minutes — a human recruiter reads every application."
      />
      <section className="max-w-7xl mx-auto px-6">
        <SectionTag>Careers</SectionTag>
        <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-slate-900 max-w-4xl">
          <MaskLine delay={0.15}>Open roles worth</MaskLine>
          <MaskLine delay={0.3}>
            <span className="font-editorial italic font-medium text-brand">your next chapter.</span>
          </MaskLine>
        </h1>
        <FadeUp delay={0.5} className="mt-8 max-w-2xl">
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            Every role below is a live mandate with a real client. Apply in two minutes —
            a human recruiter reads every application.
          </p>
        </FadeUp>

        <FadeUp delay={0.6} className="mt-14">
          <div className="flex flex-col lg:flex-row gap-4 rounded-2xl border border-slate-200/90 bg-white p-3 shadow-[0_12px_36px_rgba(15,23,42,0.06)]" data-testid="job-filters">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search roles, companies, skills..."
                data-testid="job-search-input"
                className="h-12 rounded-full border-transparent bg-transparent pl-11 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand"
              />
            </div>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="filter-industry-select" className="h-12 lg:w-64 rounded-full border-slate-200 bg-slate-50 text-slate-700 focus:ring-brand">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent className={SELECT_CONTENT_LIGHT}>
                <SelectItem value="all" data-testid="industry-option-all">All industries</SelectItem>
                {INDUSTRIES.map((i) => (
                  <SelectItem key={i} value={i} data-testid={`industry-option-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="filter-type-select" className="h-12 lg:w-52 rounded-full border-slate-200 bg-slate-50 text-slate-700 focus:ring-brand">
                <SelectValue placeholder="Job type" />
              </SelectTrigger>
              <SelectContent className={SELECT_CONTENT_LIGHT}>
                <SelectItem value="all" data-testid="type-option-all">All types</SelectItem>
                {JOB_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`type-option-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FadeUp>

        <div className="mt-16">
          {jobs === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="jobs-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 rounded-2xl border border-slate-200 bg-white animate-pulse" />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center" data-testid="jobs-empty">
              <p className="font-editorial italic text-3xl text-slate-400">No roles match that filter.</p>
              <p className="mt-3 text-sm text-slate-500">Try clearing a filter — or send us your resume via the contact page and we&rsquo;ll keep you warm.</p>
            </div>
          ) : (
            <>
              <p className="mb-6 text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="jobs-count">
                {jobs.length} open role{jobs.length === 1 ? "" : "s"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {jobs.map((j, i) => (
                  <JobCard key={j.id} job={j} index={i} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
